import React from 'react';
import styled from 'styled-components';
import { ShoppingCart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useGlobalOrderData } from '../../../hooks/useGlobalOrderData';
import { ThemeColors, Spacing, Typography, BorderRadius, Shadows } from '../../../styles/ThemeStyles';
import { getTranslatedProductName } from '../../../utils/translationHelpers';

const GlobalOrderSummaryContainer = styled.div`
  background: linear-gradient(135deg, ${ThemeColors.success.light} 0%, ${ThemeColors.success.main} 100%);
  border-radius: ${BorderRadius.lg};
  padding: ${Spacing.lg};
  margin: ${Spacing.lg} 0;
  box-shadow: ${Shadows.md};
  border: 1px solid ${ThemeColors.success.main};
`;

const SummaryHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.md};
  padding-bottom: ${Spacing.sm};
  border-bottom: 2px solid rgba(255, 255, 255, 0.3);
`;

const SummaryIcon = styled(ShoppingCart)`
  color: white;
  background: rgba(255, 255, 255, 0.2);
  padding: ${Spacing.xs};
  border-radius: ${BorderRadius.sm};
`;

const SummaryTitle = styled.h3`
  color: white;
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
  margin: 0;
`;

const SummaryItems = styled.div`
  margin-bottom: ${Spacing.md};
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.sm} 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  
  &:last-child {
    border-bottom: none;
  }
`;

const ItemName = styled.span`
  color: white;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.medium};
  flex: 1;
`;

const ItemPrice = styled.span`
  color: white;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  background: rgba(255, 255, 255, 0.2);
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
`;

const SummaryTotal = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.md} 0;
  border-top: 2px solid rgba(255, 255, 255, 0.3);
  margin-top: ${Spacing.sm};
`;

const TotalLabel = styled.span`
  color: white;
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.bold};
`;

const TotalAmount = styled.span`
  color: white;
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  background: rgba(255, 255, 255, 0.3);
  padding: ${Spacing.sm} ${Spacing.md};
  border-radius: ${BorderRadius.md};
`;

const EmptyState = styled.div`
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  padding: ${Spacing.lg};
  font-style: italic;
`;

const CategoryBadge = styled.span<{ category: string }>`
  display: inline-block;
  padding: 2px ${Spacing.xs};
  border-radius: ${BorderRadius.sm};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  margin-right: ${Spacing.xs};
  background: rgba(255, 255, 255, 0.2);
  color: white;
  
  ${props => {
    switch (props.category) {
      case 'heritage':
        return 'background: rgba(139, 69, 19, 0.8);';
      case 'livingston':
        return 'background: rgba(0, 100, 0, 0.8);';
      case 'flower':
        return 'background: rgba(255, 192, 203, 0.8); color: #333;';
      case 'vegetable':
        return 'background: rgba(34, 139, 34, 0.8);';
      case 'supply':
        return 'background: rgba(128, 128, 128, 0.8);';
      default:
        return 'background: rgba(255, 255, 255, 0.2);';
    }
  }}
`;

const FormSubtotalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.sm} 0;
  margin: ${Spacing.sm} 0;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
`;

const FormSubtotalTitle = styled.span`
  color: white;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  background: rgba(255, 255, 255, 0.2);
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
`;

const FormSubtotalAmount = styled.span`
  color: white;
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.bold};
  background: rgba(255, 255, 255, 0.3);
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.sm};
`;

interface GlobalOrderSummaryProps {
  /** Type of order - 'store-order' shows only product items, 'supplies' shows only supply items */
  orderType?: 'store-order' | 'supplies';
}

export const GlobalOrderSummary: React.FC<GlobalOrderSummaryProps> = ({ orderType = 'store-order' }) => {
  const { t } = useTranslation();
  const { orderData } = useGlobalOrderData();
  
  // Create a hash string that includes item IDs and quantities for proper change detection
  // This ensures the component re-renders when quantities change, not just when items are added/removed
  // The hash changes whenever an item's quantity changes, triggering a re-render
  const createItemsHash = React.useCallback((items: any[] | undefined): string => {
    if (!items || items.length === 0) return '';
    // Sort by ID for consistent hashing, then create hash string with ID and quantity
    return items
      .sort((a, b) => (a.id || '').localeCompare(b.id || ''))
      .map(item => `${item.id}:${item.quantity}`)
      .join('|');
  }, []);
  
  // Create hashes for each item array - these will change when quantities change
  const heritageItemsHash = createItemsHash(orderData.heritageItems);
  const livingstonItemsHash = createItemsHash(orderData.livingstonItems);
  const flowerItemsHash = createItemsHash(orderData.flowerItems);
  const vegetableItemsHash = createItemsHash(orderData.vegetableItems);
  const supplyItemsHash = createItemsHash(orderData.supplyItems);
  
  // Memoize item arrays with hash-based dependency tracking
  // This ensures real-time updates when quantities change, not just when items are added/removed
  const heritageItems = React.useMemo(() => {
    return orderData.heritageItems || [];
  }, [orderData.heritageItems, heritageItemsHash]);
  
  const livingstonItems = React.useMemo(() => {
    return orderData.livingstonItems || [];
  }, [orderData.livingstonItems, livingstonItemsHash]);
  
  const flowerItems = React.useMemo(() => {
    return orderData.flowerItems || [];
  }, [orderData.flowerItems, flowerItemsHash]);
  
  const vegetableItems = React.useMemo(() => {
    return orderData.vegetableItems || [];
  }, [orderData.vegetableItems, vegetableItemsHash]);
  
  const supplyItems = React.useMemo(() => {
    return orderData.supplyItems || [];
  }, [orderData.supplyItems, supplyItemsHash]);

  // Calculate totals for each form (memoized)
  const { heritageTotal, heritageItemCount } = React.useMemo(() => ({
    heritageTotal: heritageItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    heritageItemCount: heritageItems.reduce((sum, item) => sum + item.quantity, 0)
  }), [heritageItems]);

  const { livingstonTotal, livingstonItemCount } = React.useMemo(() => ({
    livingstonTotal: livingstonItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    livingstonItemCount: livingstonItems.reduce((sum, item) => sum + item.quantity, 0)
  }), [livingstonItems]);

  const { flowerTotal, flowerItemCount } = React.useMemo(() => ({
    flowerTotal: flowerItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    flowerItemCount: flowerItems.reduce((sum, item) => sum + item.quantity, 0)
  }), [flowerItems]);

  const { vegetableTotal, vegetableItemCount } = React.useMemo(() => ({
    vegetableTotal: vegetableItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    vegetableItemCount: vegetableItems.reduce((sum, item) => sum + item.quantity, 0)
  }), [vegetableItems]);

  const { supplyTotal, supplyItemCount } = React.useMemo(() => ({
    supplyTotal: supplyItems.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    supplyItemCount: supplyItems.reduce((sum, item) => sum + item.quantity, 0)
  }), [supplyItems]);

  // Overall totals (memoized) - filter based on orderType
  const { totalItems, totalAmount } = React.useMemo(() => {
    if (orderType === 'supplies') {
      // For supplies, only count supply items
      return {
        totalItems: supplyItemCount,
        totalAmount: supplyTotal
      };
    } else {
      // For store orders, only count product items (exclude supplies)
      return {
        totalItems: heritageItemCount + livingstonItemCount + flowerItemCount + vegetableItemCount,
        totalAmount: heritageTotal + livingstonTotal + flowerTotal + vegetableTotal
      };
    }
  }, [orderType, heritageItemCount, livingstonItemCount, flowerItemCount, vegetableItemCount, supplyItemCount, heritageTotal, livingstonTotal, flowerTotal, vegetableTotal, supplyTotal]);

  const getCategoryLabel = (category: string) => {
    switch (category) {
      case 'heritage': return 'H';
      case 'livingston': return 'L';
      case 'flower': return 'F';
      case 'vegetable': return 'V';
      case 'supply': return 'S';
      default: return '?';
    }
  };

  const renderFormSection = (items: any[], category: string, formName: string) => {
    if (items.length === 0) return null;

    const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

    // Translate category name
    const translatedCategoryName = t(`categories.${category}`);
    const itemLabel = itemCount === 1 ? t('common.item') : t('common.items');

    return (
      <div key={category}>
        <FormSubtotalHeader>
          <FormSubtotalTitle>{translatedCategoryName} {t('forms.subtotal')} ({itemCount} {itemLabel})</FormSubtotalTitle>
          <FormSubtotalAmount>${total.toFixed(2)}</FormSubtotalAmount>
        </FormSubtotalHeader>
        {items.map((item, index) => {
          // Translate product name (stored in English, displayed translated)
          const translatedProductName = getTranslatedProductName(item.packetDescription || '', t);
          return (
            <SummaryItem key={index}>
              <ItemName>
                <CategoryBadge category={category}>
                  {getCategoryLabel(category)}
                </CategoryBadge>
                {translatedProductName} {item.code && <span style={{ fontSize: '0.85em', opacity: 0.9 }}>({t('pdf.code')}: {item.code})</span>} x {item.quantity}
              </ItemName>
              <ItemPrice>${(item.price * item.quantity).toFixed(2)}</ItemPrice>
            </SummaryItem>
          );
        })}
      </div>
    );
  };

  const itemLabel = totalItems === 1 ? t('common.item') : t('common.items');

  return (
    <GlobalOrderSummaryContainer>
      <SummaryHeader>
        <SummaryIcon size={24} />
        <SummaryTitle>{t('forms.orderSummary')}</SummaryTitle>
      </SummaryHeader>
      
      <SummaryItems>
        {totalItems === 0 ? (
          <EmptyState>{t('orderPreview.noProductsSelected')}</EmptyState>
        ) : (
          <>
            {orderType === 'supplies' ? (
              // For supplies, only show supply items
              renderFormSection(supplyItems, 'supply', t('categories.supply', 'Supplies'))
            ) : (
              // For store orders, only show product items (exclude supplies)
              <>
                {renderFormSection(heritageItems, 'heritage', t('categories.heritage'))}
                {renderFormSection(livingstonItems, 'livingston', t('categories.livingston'))}
                {renderFormSection(flowerItems, 'flower', t('categories.flower'))}
                {renderFormSection(vegetableItems, 'vegetable', t('categories.vegetable'))}
              </>
            )}
          </>
        )}
      </SummaryItems>
      
      <SummaryTotal>
        <TotalLabel>{t('forms.total')} ({totalItems} {itemLabel})</TotalLabel>
        <TotalAmount>${totalAmount.toFixed(2)}</TotalAmount>
      </SummaryTotal>
    </GlobalOrderSummaryContainer>
  );
};
